import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { HeroBackdrop } from "@/components/site/HeroBackdrop";
import { Link } from "react-router-dom";
import { ArrowDownRight, Play, Sparkles, ArrowRight } from "lucide-react";
import { MaskLine, Overline } from "./primitives";
import { MARQUEE_ITEMS } from "../../data/site";
import { useFreshContent } from "@/hooks/useFreshContent";
import { MapsMenu } from "./MapsMenu";

const ease = [0.22, 1, 0.36, 1];

const trackHeroTag = (label) => {
  const body = JSON.stringify({ events: [{ path: "/", section: `Hero link: ${label}`, dwell_ms: 0 }] });
  try { navigator.sendBeacon(`${process.env.REACT_APP_BACKEND_URL}/api/heat/batch`, new Blob([body], { type: "application/json" })); }
  catch { /* non-blocking */ }
};

const HiringChip = () => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/careers/jobs`)
      .then((r) => (r.ok ? r.json() : [])).then((l) => setCount(l.length)).catch(() => {});
  }, []);
  if (!count) return null;
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease, delay: 1.25 }}>
      <Link to="/careers#openings" data-testid="hero-hiring-banner"
        className="group inline-flex items-center gap-2 border border-gf-primary/40 bg-gf-primary/[0.07] px-4 py-2 mt-6 hover:bg-gf-primary/15 transition-colors">
        <Sparkles className="w-3.5 h-3.5 text-gf-primary" />
        <span className="font-mono text-[10px] uppercase tracking-wider text-gf-primary">
          We're hiring — {count} open role{count > 1 ? "s" : ""}
        </span>
        <ArrowRight className="w-3.5 h-3.5 text-gf-primary group-hover:translate-x-0.5 transition-transform" />
      </Link>
    </motion.div>
  );
};

const HeroQuote = () => {
  const [quotes, setQuotes] = useState([]);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/case-studies?limit=10`)
      .then((r) => (r.ok ? r.json() : [])).then((l) => setQuotes(l.filter((c) => c.quote))).catch(() => {});
  }, []);
  useEffect(() => {
    if (quotes.length < 2) return;
    const t = setInterval(() => setIdx((i) => i + 1), 7000);
    return () => clearInterval(t);
  }, [quotes]);
  if (!quotes.length) return null;
  const q = quotes[idx % quotes.length];
  return (
    <motion.div key={q.id + "-" + idx} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease }} className="mt-8 border-l-2 border-gf-primary pl-4 max-w-xl"
      data-testid="hero-testimonial">
      <p className="font-body text-sm italic text-gf-text/85 leading-relaxed">"{q.quote}"</p>
      {q.quote_author && <p className="font-mono text-[10px] uppercase tracking-wider text-gf-muted mt-1.5">— {q.quote_author}</p>}
    </motion.div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const fresh = useFreshContent("home");
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      {/* Animated background */}
      <motion.div style={{ y: yBg }} className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gf-bg" />
        {/* theme-aware backdrop — video/photo follows the headline's subject */}
        <HeroBackdrop fresh={fresh} />
        <div className="absolute inset-0 bg-gradient-to-b from-gf-bg/55 via-gf-bg/25 to-gf-bg" />
        {/* grid */}
        <div
          className="absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage:
              "linear-gradient(rgb(var(--gf-primary) / 0.35) 1px, transparent 1px), linear-gradient(90deg, rgb(var(--gf-primary) / 0.35) 1px, transparent 1px)",
            backgroundSize: "68px 68px",
            maskImage: "radial-gradient(ellipse 80% 70% at 70% 40%, #000 30%, transparent 75%)",
            WebkitMaskImage: "radial-gradient(ellipse 80% 70% at 70% 40%, #000 30%, transparent 75%)",
          }}
        />
        {/* glow orbs */}
        <div className="gf-orb absolute top-[12%] right-[8%] w-[46vw] h-[46vw] max-w-[640px] max-h-[640px] rounded-full bg-gf-primary/20 blur-[130px]" />
        <div className="gf-orb absolute bottom-[6%] left-[-6%] w-[36vw] h-[36vw] rounded-full bg-emerald-500/10 blur-[130px]" style={{ animationDelay: "-6s" }} />
        {/* rotating wireframe rings */}
        <div className="absolute top-1/2 right-[6%] -translate-y-1/2 hidden lg:block">
          <div className="gf-ring w-[420px] h-[420px] rounded-full border border-gf-primary/25" />
          <div className="gf-ring-rev absolute inset-8 rounded-full border border-white/10" />
          <div className="gf-ring absolute inset-16 rounded-full border border-gf-primary/15" style={{ animationDuration: "28s" }} />
        </div>
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 max-w-[1400px] mx-auto w-full px-5 sm:px-8 pb-16 pt-32">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
          <Overline>
            {[
              ["RE Advisory", "/coreservices/re-storage-advisory", "advisory"],
              ["Engineering", "/coreservices/transmission-engineering", "engineering"],
              ["EPC", "/coreservices/epc", "epc"],
              ["Financing", "/coreservices/green-financing", "financing"],
              ["Asset Monetisation & PPP", "/coreservices/asset-monetisation-ppp", "ppp"],
            ].map(([label, to, key], i) => (
              <span key={key}>
                {i > 0 && <span className="mx-2 opacity-50">·</span>}
                <Link to={to} data-testid={`hero-tag-${key}`} onClick={() => trackHeroTag(label)}
                  className="hover:text-gf-text underline-offset-4 hover:underline transition-colors">{label}</Link>
              </span>
            ))}
          </Overline>
        </motion.div>

        <h1 className="font-heading font-black text-gf-text tracking-tighter leading-[0.86] mt-6 text-[15vw] sm:text-[12vw] lg:text-[8.5rem]" data-testid="hero-headline">
          <MaskLine delay={0.45}>{fresh?.headline_a || "Energy"}</MaskLine>
          <MaskLine delay={0.58}>
            {fresh?.headline_b || "transition,"} <span className="text-gf-primary">{fresh?.accent || "engineered."}</span>
          </MaskLine>
        </h1>

        <div className="mt-10 grid lg:grid-cols-12 gap-8 items-end">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease, delay: 0.9 }}
            className="font-body text-gf-muted text-base md:text-lg leading-relaxed lg:col-span-5 max-w-xl"
            data-testid="hero-sub"
          >
            {fresh?.sub || `Growthifye advises, engineers and finances the clean-energy economy — from renewables and
            battery storage to transmission networks, green financing and project debt syndication.`}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease, delay: 1.05 }}
            className="lg:col-span-7 flex flex-col sm:flex-row sm:flex-wrap gap-3 lg:justify-end"
          >
            <button
              type="button"
              data-testid="hero-watch-story"
              onClick={() => window.dispatchEvent(new CustomEvent("open-brand-film"))}
              className="group inline-flex items-center justify-center gap-2.5 border border-white/25 text-gf-text font-body font-bold text-base px-7 py-4 hover:border-gf-primary hover:text-gf-primary transition-colors duration-200"
            >
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-gf-primary text-gf-bg">
                <Play className="w-3 h-3 ml-0.5" fill="currentColor" />
              </span>
              Watch our story
            </button>
            <MapsMenu />
            <a
              href="#contact"
              data-testid="hero-cta"
              className="group inline-flex items-center justify-center gap-2 whitespace-nowrap bg-gf-primary text-gf-bg font-body font-bold text-base px-7 py-4 hover:bg-gf-primary-hover transition-colors duration-200"
            >
              Start a conversation
              <ArrowDownRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:translate-y-0.5 transition-transform duration-200" />
            </a>
          </motion.div>
        </div>
        <HiringChip />
        <HeroQuote />
      </motion.div>

      {/* marquee */}
      <div className="relative z-10 border-y border-white/5 bg-gf-bg/50 backdrop-blur-sm py-4 overflow-hidden">
        <div className="gf-marquee gf-marquee-slow" data-testid="hero-marquee">
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
            <span key={i} className="flex items-center font-heading font-bold text-lg text-gf-muted uppercase tracking-wide whitespace-nowrap">
              <Link to={item.to} data-testid={`hero-marquee-${i}`} className="px-8 hover:text-gf-primary transition-colors">{item.label}</Link>
              <span className="w-1.5 h-1.5 rounded-full bg-gf-primary" />
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};
